import { create } from 'zustand';

import { BoardInput, CollageInput, GameInput, Space, SpaceType, WallInput } from '../mainApi';
import { SpacesService } from '../services/spaces.service';
import { SpaceDialogState } from '../types';
import { trackAnalytics, trackAnalyticsOnce } from '@dotstorming/analytics';

export type SpaceFormState = {
  title: string;
  workspaceTitle: string;
  description: string;
  password: string;
};

type SpacesState = {
  spaces: Space[];
  isLoadingSpaces: boolean;
  isSavingSpaces: boolean;
  spacesError: string | null;
  spaceDialog: SpaceDialogState;
  spaceForm: SpaceFormState;
  selectedSpaceForMenu: Space | null;
  loadSpaces: () => Promise<void>;
  addSpace: (
    spaceType: SpaceType,
    input: BoardInput | WallInput | CollageInput | GameInput
  ) => Promise<string | null>;
  editSpace: (
    space: Space,
    input: BoardInput | WallInput | CollageInput | GameInput
  ) => Promise<void>;
  copySpace: (
    space: Space,
    input: BoardInput | WallInput | CollageInput | GameInput
  ) => Promise<void>;
  archiveSpace: (space: Space, archived: boolean) => Promise<void>;
  removeSpace: (space: Space) => Promise<void>;
  setThumbnail: (space: Space, file: File) => Promise<void>;
  openSpaceDialog: (payload: {
    mode: SpaceDialogState['mode'];
    spaceType: SpaceType;
    space: Space | null;
    initialForm: SpaceFormState;
  }) => void;
  closeSpaceDialog: () => void;
  setSpaceForm: (value: Partial<SpaceFormState>) => void;
  setSpaceMenuSpace: (space: Space | null) => void;
};

const getSpaceId = (space: Space) => space.id ?? space._id ?? '';

const mapSpaces = (spaces: Space[], existingSpaces: Space[] = []) =>
  spaces.map((space) => {
    const id = getSpaceId(space);
    const existingSpace = existingSpaces.find((item) => getSpaceId(item) === id);

    return {
      ...space,
      id,
      thumbnailCacheBust: existingSpace?.thumbnailCacheBust,
    };
  });

const withSave = async (
  set: (value: Partial<SpacesState>) => void,
  action: () => Promise<void>
) => {
  set({ isSavingSpaces: true, spacesError: null });

  try {
    await action();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to save space changes.';
    set({ spacesError: message });
    throw error;
  } finally {
    set({ isSavingSpaces: false });
  }
};

export const useSpacesStore = create<SpacesState>((set, get) => ({
  spaces: [],
  isLoadingSpaces: true,
  isSavingSpaces: false,
  spacesError: null,
  spaceDialog: {
    mode: 'create',
    open: false,
    spaceType: 'board',
    space: null,
  },
  spaceForm: {
    title: '',
    workspaceTitle: '',
    description: '',
    password: '',
  },
  selectedSpaceForMenu: null,
  loadSpaces: async () => {
    set({ isLoadingSpaces: true, spacesError: null });

    try {
      set({
        spaces: mapSpaces(await SpacesService.getAll(), get().spaces),
        isLoadingSpaces: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load spaces.';
      set({ spacesError: message, isLoadingSpaces: false });
    }
  },
  addSpace: async (spaceType, input) => {
    let path: string | null = null;
    const ownedBoardCount = get().spaces.filter(
      (space) => space.type === 'board' && space.isOwner === true
    ).length;

    await withSave(set, async () => {
      const result = await SpacesService.create(spaceType, input);

      path = result.path;

      if (spaceType === 'board') {
        const workspaceContext = input.workspace ? 'workspace' : 'personal';
        trackAnalytics('board_created', {
          creation_source: 'dashboard',
          from_template: false,
          workspace_context: workspaceContext,
        });
        if (ownedBoardCount === 1) {
          trackAnalyticsOnce('second_board_created', 'second_board_created', {
            creation_source: 'dashboard',
            from_template: false,
          });
        }
      }

      await get().loadSpaces();
    });

    return path;
  },
  editSpace: async (space, input) => {
    await withSave(set, async () => {
      await SpacesService.edit(space, input);
      await get().loadSpaces();
    });
  },
  copySpace: async (space, input) => {
    await withSave(set, async () => {
      await SpacesService.copy(space, input);
      await get().loadSpaces();
    });
  },
  archiveSpace: async (space, archived) => {
    await withSave(set, async () => {
      await SpacesService.archive(space, archived);
      await get().loadSpaces();
    });
  },
  removeSpace: async (space) => {
    await withSave(set, async () => {
      await SpacesService.remove(space);
      await get().loadSpaces();
    });
  },
  setThumbnail: async (space, file) => {
    await withSave(set, async () => {
      await SpacesService.setThumbnail(space, file);
      await get().loadSpaces();

      const spaceId = getSpaceId(space);
      const thumbnailCacheBust = Date.now();

      set((state) => ({
        spaces: state.spaces.map((item) =>
          getSpaceId(item) === spaceId ? { ...item, thumbnailCacheBust } : item
        ),
      }));
    });
  },
  openSpaceDialog: ({ mode, spaceType, space, initialForm }) =>
    set({
      spaceDialog: {
        mode,
        open: true,
        spaceType,
        space,
      },
      spaceForm: initialForm,
    }),
  closeSpaceDialog: () =>
    set((state) => ({
      spaceDialog: {
        ...state.spaceDialog,
        open: false,
      },
    })),
  setSpaceForm: (value) =>
    set((state) => ({
      spaceForm: {
        ...state.spaceForm,
        ...value,
      },
    })),
  setSpaceMenuSpace: (space) =>
    set({
      selectedSpaceForMenu: space,
    }),
}));

export const getSpaceUrl = (space: Space, workspaceId?: string | null): string => {
  const id = getSpaceId(space);
  const params = new URLSearchParams();
  const trimmedWorkspaceId = workspaceId?.trim();

  if (trimmedWorkspaceId) {
    params.set('workspaceId', trimmedWorkspaceId);
  }

  const queryString = params.toString();
  const query = queryString ? `?${queryString}` : '';

  if (space.type === 'wall') {
    return `/w/${id}${query}`;
  }

  if (space.type === 'collage') {
    return `/c/${id}${query}`;
  }

  if (space.type === 'game') {
    return `/g/${id}/cards${query}`;
  }

  return `/b/${id}${query}`;
};
